import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Build as BuildIcon,
  Assignment as TestCasesIcon,
  Explore as ExploreIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

const MetricCard = ({ title, value, subtitle, icon, color, trend, onClick }) => (
  <Card
    sx={{
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: (theme) => `0 8px 25px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.15)'}`,
      },
      '&:focus-visible': {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: '2px',
      },
    }}
    onClick={onClick}
    tabIndex={onClick ? 0 : -1}
  >
    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Avatar
          sx={{
            bgcolor: `${color}.light`,
            color: `${color}.main`,
            width: 56,
            height: 56,
            boxShadow: 2,
          }}
        >
          {icon}
        </Avatar>
        <IconButton 
          size="small"
          sx={{ 
            opacity: 0.6,
            '&:hover': { opacity: 1 },
            transition: 'opacity 0.2s ease'
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>
      
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Box>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800, 
              mb: 1,
              fontSize: '2.5rem',
              lineHeight: 1.1,
            }}
          >
            {value}
          </Typography>
          
          <Typography 
            variant="h6" 
            color="text.primary" 
            sx={{ 
              mb: 1,
              fontWeight: 600,
              fontSize: '1.1rem',
            }}
          >
            {title}
          </Typography>
          
          {subtitle && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                lineHeight: 1.4,
                fontSize: '0.875rem',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        
        {trend && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mt: 2,
              animation: 'fadeInUp 0.5s ease-out',
              '@keyframes fadeInUp': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(10px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            <TrendingUpIcon
              sx={{
                fontSize: 18,
                color: trend > 0 ? 'success.main' : 'error.main',
                mr: 0.75,
              }}
            />
            <Typography
              variant="body2"
              color={trend > 0 ? 'success.main' : 'error.main'}
              sx={{ 
                fontWeight: 700,
                fontSize: '0.875rem',
              }}
            >
              {trend > 0 ? '+' : ''}{trend}%
            </Typography>
          </Box>
        )}
      </Box>
    </CardContent>
  </Card>
);

const ActionCard = ({ title, description, icon, color, action, onClick, tooltip, disabled = false }) => (
  <Tooltip 
    title={tooltip || description} 
    placement="top"
    arrow
    sx={{
      '& .MuiTooltip-tooltip': {
        fontSize: '0.875rem',
        maxWidth: 300,
      },
    }}
  >
    <Card
      sx={{
        height: '100%',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: disabled ? 0.6 : 1,
        '&:hover': {
          transform: disabled ? 'none' : 'translateY(-4px) scale(1.02)',
          boxShadow: disabled ? 'none' : (theme) => `0 12px 40px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.2)'}`,
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
        },
      }}
      onClick={disabled ? undefined : onClick}
      tabIndex={disabled ? -1 : 0}
    >
      <CardContent sx={{ 
        textAlign: 'center', 
        py: 5, 
        px: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: 280,
      }}>
        <Box>
          <Avatar
            sx={{
              bgcolor: `${color}.light`,
              color: `${color}.main`,
              width: 72,
              height: 72,
              mx: 'auto',
              mb: 3,
              boxShadow: 3,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            {icon}
          </Avatar>
          
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              mb: 2,
              fontSize: '1.25rem',
            }}
          >
            {title}
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 4,
              lineHeight: 1.6,
              fontSize: '0.9rem',
            }}
          >
            {description}
          </Typography>
        </Box>
        
        {disabled ? (
          <span>
            <Button
              variant="contained"
              color={color}
              startIcon={action.icon}
              disabled={disabled}
              sx={{ 
                borderRadius: 3,
                py: 1.5,
                px: 3,
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 2,
                  transform: 'none',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {action.label}
            </Button>
          </span>
        ) : (
          <Button
            variant="contained"
            color={color}
            startIcon={action.icon}
            sx={{ 
              borderRadius: 3,
              py: 1.5,
              px: 3,
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {action.label}
          </Button>
        )}
      </CardContent>
    </Card>
  </Tooltip>
);

const ProgressCard = ({ title, progress, status, details }) => (
  <Card 
    sx={{ 
      height: '100%',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: (theme) => `0 6px 20px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)'}`,
      },
      '&:focus-visible': {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: '2px',
      },
    }}
    tabIndex={0}
  >
    <CardContent sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700,
            fontSize: '1.1rem',
          }}
        >
          {title}
        </Typography>
        <Chip
          label={status}
          size="small"
          color={
            status === 'Completed' ? 'success' :
            status === 'In Progress' ? 'warning' :
            status === 'Failed' ? 'error' : 'default'
          }
          sx={{
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        />
      </Box>
      
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 10,
          borderRadius: 5,
          mb: 3,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 5,
            transition: 'transform 0.5s ease',
          },
        }}
      />
      
      <Typography 
        variant="body2" 
        color="text.secondary"
        sx={{
          lineHeight: 1.5,
          fontSize: '0.875rem',
        }}
      >
        {details}
      </Typography>
    </CardContent>
  </Card>
);

const ModernDashboard = ({
  projectSummary,
  onStartExploration,
  onGenerateAutomation,
  onRunAutomation,
  explorationStatus,
  automationResults,
  onRefreshData,
  onOpenQuality = () => {},
}) => {
  const metrics = [
    {
      title: 'Total Test Cases',
      value: projectSummary?.statistics?.total_test_cases || 0,
      subtitle: 'Generated from UI exploration',
      icon: <TestCasesIcon />,
      color: 'primary',
      trend: 12,
    },
    {
      title: 'Automation Scripts',
      value: projectSummary?.statistics?.total_automation_scripts || 0,
      subtitle: 'Playwright scripts generated',
      icon: <BuildIcon />,
      color: 'secondary',
      trend: 8,
    },
    {
      title: 'Success Rate',
      value: `${projectSummary?.statistics?.success_rate || 0}%`,
      subtitle: 'Test execution success',
      icon: <CheckCircleIcon />,
      color: 'success',
      trend: 5,
    },
    {
      title: 'Coverage',
      value: `${projectSummary?.statistics?.coverage || 0}%`,
      subtitle: 'UI elements covered',
      icon: <ExploreIcon />,
      color: 'info',
      trend: -2,
    },
  ];

  const actions = [
    {
      title: 'UI Exploration',
      description: 'Explore and analyze UI elements to generate test cases',
      tooltip: 'Start an automated exploration of your application to discover UI elements, features, and user flows. This will generate comprehensive test cases.',
      icon: <ExploreIcon />,
      color: 'primary',
      action: {
        label: 'Start Exploration',
        icon: <PlayIcon />,
      },
      onClick: onStartExploration,
    },
    {
      title: 'Generate Automation',
      description: 'Create automated test scripts from test cases',
      tooltip: 'Convert your approved test cases into executable automation scripts using Playwright, Cypress, or Selenium frameworks.',
      icon: <BuildIcon />,
      color: 'secondary',
      action: {
        label: 'Generate Scripts',
        icon: <BuildIcon />,
      },
      onClick: onGenerateAutomation,
    },
    {
      title: 'Run Tests',
      description: 'Execute automated tests and view results',
      tooltip: 'Execute your automation scripts in headed or headless mode and view detailed test results, screenshots, and performance metrics.',
      icon: <PlayIcon />,
      color: 'success',
      action: {
        label: projectSummary?.statistics?.total_automation_scripts > 0 ? 'Run Tests' : 'No Scripts',
        icon: <PlayIcon />,
      },
      onClick: onRunAutomation,
      disabled: projectSummary?.statistics?.total_automation_scripts === 0,
    },
  ];

  const progressItems = [
    {
      title: 'UI Exploration',
      progress: explorationStatus?.status === 'completed' ? 100 : 
                explorationStatus?.status === 'in_progress' ? 65 : 0,
      status: explorationStatus?.status === 'completed' ? 'Completed' :
              explorationStatus?.status === 'in_progress' ? 'In Progress' : 'Not Started',
      details: explorationStatus?.status === 'completed' ? 
               'All UI elements explored and test cases generated' :
               explorationStatus?.status === 'in_progress' ? 
               'Exploring UI elements and generating test cases' : 
               'Ready to start UI exploration',
    },
    {
      title: 'Automation Generation',
      progress: projectSummary?.statistics?.total_automation_scripts > 0 ? 100 : 0,
      status: projectSummary?.statistics?.total_automation_scripts > 0 ? 'Completed' : 'Not Started',
      details: projectSummary?.statistics?.total_automation_scripts > 0 ? 
               `${projectSummary?.statistics?.total_automation_scripts} automation scripts generated` :
               'Ready to generate automation scripts',
    },
  ];

  return (
    <Box sx={{ p: 4, pl: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: '2.25rem' }}>
            Dashboard Overview
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
            Monitor your project progress and manage automation workflows
          </Typography>
        </Box>
        {onRefreshData && (
          <Button
            variant="outlined"
            onClick={onRefreshData}
            sx={{ ml: 2 }}
          >
            🔄 Refresh Data
          </Button>
        )}
      </Box>

      {/* Metrics Grid */}
      <Grid container spacing={4} sx={{ mb: 5 }}>
        {metrics.map((metric, index) => (
          <Grid key={index} xs={12} sm={6} md={3}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      {/* Action Cards */}
      <Grid container spacing={4} sx={{ mb: 5 }}>
        {actions.map((action, index) => (
          <Grid key={index} xs={12} md={4}>
            <ActionCard 
              {...action} 
              tooltip={action.tooltip}
              disabled={action.disabled}
            />
          </Grid>
        ))}
      </Grid>

      {/* Progress Cards */}
      <Grid container spacing={4}>
        {progressItems.map((item, index) => (
          <Grid key={index} xs={12} md={6}>
            <ProgressCard {...item} />
          </Grid>
        ))}
      </Grid>

      <Card
        variant="outlined"
        sx={{
          mt: 6,
          borderRadius: 3,
          borderStyle: 'dashed',
          p: 4,
          textAlign: 'center',
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.03)'
              : 'linear-gradient(135deg, rgba(25,118,210,0.08), rgba(123,31,162,0.08))',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Quality analytics moved
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}>
          Explore defect density, velocity trends, MTTR, and metadata coverage in the new Quality tab. It uses the same
          Product Knowledge dataset—no re-ingest needed.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onOpenQuality}
          sx={{ borderRadius: 999, px: 4 }}
        >
          Open quality dashboard
        </Button>
      </Card>
    </Box>
  );
};

export default ModernDashboard; 